import BaseNMComDataObject from "objects/base.nmcom_data.object";
import BaseModel from "models/base.model";
import BaseNMComAdapter from "adapters/base.nmcom.adapter";
import {isArray, isObject, isNumber, isString, isEmpty} from "utils/checking.util";

const DEFAULT_PAGE_NUMBER = 0;
const DEFAULT_PAGE_SIZE = 20;
const APPEND = "append";
const PREPEND = "prepend";
const REPLACE = "replace";

export default class BaseNMComCollection extends BaseNMComDataObject {
	static model = BaseModel;
	static adapter = BaseNMComAdapter;
	endpoint;
	models;
	resultCount;
	pageNumber;
	pageSize;
	params;

	static APPEND = APPEND;
	static PREPEND = PREPEND;
	static REPLACE = REPLACE;

	// TODO: Implement this symbol/generator so we can make this class iterable
	// *[Symbol.iterator]() {
	// 	return this.models.values();
	// }

	static transformResponseData(data) {
		if(!isArray(data)) {
			data = [];
		}

		data = data.map( (datum) => {
			return this.model.transformResponseData(datum);
		});

		return data;
	}

	static serializeRequestData(data) {
		return data;
	}

	get length() {
		return this.models.length;
	}

	get all() {
		return ( this.models || [] );
	}

	constructor(data) {
		super(data);

		this.models = [];
		this.endpoint = this.endpoint || this.constructor.model.endpoint;
		this.pageNumber = DEFAULT_PAGE_NUMBER;
		this.pageSize = DEFAULT_PAGE_SIZE;
		this.resultCount = 0;
		this.params = {};
		this.fill(data);
	}

	// Fills this collection with raw json data and converts it to the defined model
	fill(data, method=REPLACE) {
		if(!isArray(data)) {
			if(isObject(data)) {
				data = [data];
			} else {
				data = [];
			}
		}

		let models = data.map( (datum) => {
			if(datum instanceof this.constructor.model) {
				return datum;
			}

			return new this.constructor.model(datum);
		});

		if(method === APPEND) {
			this.models.push.apply(this.models, models);
		} else if(method === PREPEND) {
			this.models.unshift.apply(this.models, models);
		} else {
			this.models = models;
		}

		return this;
	}

	serialize(properties) {
		const serializedModels = [];

		for(let index in this.models) {
			let model = this.models[index];
			serializedModels.push( model.serialize(properties) );
		}

		return serializedModels;
	}

	getResultCount() {
		return this.resultCount;
	}

	setResultCount(count) {
		if(isString(count)) {
			count = parseInt(count);
		}

		if(!isNumber(count) || count < 0) {
			count = 0;
		}

		this.resultCount = count;

		return this;
	}

	getPageNumber() {
		return this.pageNumber;
	}

	setPageNumber(number) {
		if(isString(number)) {
			number = parseInt(number);
		}

		if(!isNumber(number) || number < 0) {
			number = 0;
		}

		this.pageNumber = number;

		return this;
	}

	getPageSize() {
		return this.pageSize;
	}

	setPageSize(size) {
		if(isString(size)) {
			size = parseInt(size);
		}

		if(!isNumber(size) || size < 0) {
			size = 0;
		}

		this.pageSize = size;

		return this;
	}

	get hasNextPage() {
		return (this.pageNumber+1)*this.pageNumber < this.resultCount;
	}

	get totalPages() {
		return Math.ceil(this.resultCount / this.pageSize);
	}

	setParam(key, value) {
		this.params[key] = value;
		return this;
	}

	deleteParam(...keys) {
		keys.forEach( (key) => {
			delete this.params[key];
		});

		return this;
	}

	toggleParam(key, value) {
		if(value) {
			return this.setParam(key, value);
		} else {
			return this.deleteParam(key);
		}
	}

	hasParam(key) {
		return !isEmpty(this.params[key]);
	}

	promiseFetch(options = {}) {
		return this.constructor.adapter.promiseFetch(this, options);
	}

	promiseFetchNext(options = {}) {
		this.setPageNumber(this.getPageNumber() + 1);
		options['fillMethod'] = APPEND;
		return this.constructor.adapter.promiseFetch(this, options);
	}

	get length() {
		return this.models.length;
	}

	indexOf(model) {
		if( !(model instanceof this.constructor.model) ) {
			return -1;
		}

		return this.models.indexOf(model);
	}

	first() {
		return this.models[0];
	}

	last() {
		if(this.models.length <= 0){
			return undefined;
		}

		return this.models[this.models.length - 1]
	}

	push(model) {
		if( !(model instanceof this.constructor.model) ) {
			model = new this.constructor.model(model);
		}

		this.models.push(model);
	}

	unshift(model) {
		if( !(model instanceof this.constructor.model) ) {
			model = new this.constructor.model(model);
		}

		this.models.unshift(model);
	}

	forEach(...args) {
		return this.models.forEach(...args);
	}

	map(...args) {
		return this.models.map(...args);
	}

	slice(...args) {
		return this.models.slice(...args);
	}

	filter(...args) {
		return this.models.filter(...args);
	}

	at(index) {
		return this.models[index];
	}
}
